import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { PRICES } from '../captivision.config';
import { DEFAULT_LANGUAGE, findLanguage, matchLanguage } from './languages';

import en from './strings/en';

// The language the site is read in. A reader's choice is remembered; until they
// make one, the site follows the browser's preferred languages. The language
// lives on <html lang> and <html dir> - like the theme, so it survives moving
// between pages - and every translated string comes from useCVLanguage().

// English ships with the page. Every other language is its own small file,
// fetched the first time someone reads the site in it.
const LOADERS = {
    ar: () => import(/* webpackChunkName: "strings-ar" */ './strings/ar'),
    de: () => import(/* webpackChunkName: "strings-de" */ './strings/de'),
    es: () => import(/* webpackChunkName: "strings-es" */ './strings/es'),
    fr: () => import(/* webpackChunkName: "strings-fr" */ './strings/fr'),
    it: () => import(/* webpackChunkName: "strings-it" */ './strings/it'),
    ja: () => import(/* webpackChunkName: "strings-ja" */ './strings/ja'),
    ko: () => import(/* webpackChunkName: "strings-ko" */ './strings/ko'),
    nl: () => import(/* webpackChunkName: "strings-nl" */ './strings/nl'),
    'pt-BR': () => import(/* webpackChunkName: "strings-pt-BR" */ './strings/pt-BR'),
    'zh-Hans': () => import(/* webpackChunkName: "strings-zh-Hans" */ './strings/zh-Hans')
}

// the translations fetched so far
const loaded = { [DEFAULT_LANGUAGE]: en };

const load = code => (
    loaded[code]
        ? Promise.resolve(loaded[code])
        : LOADERS[code]().then(module => {
            loaded[code] = module.default;
            return module.default
        })
)

const LANGUAGE_KEY = 'captivision-site-language';

const readLanguage = () => {
    try {
        const saved = localStorage.getItem(LANGUAGE_KEY);

        if (saved && findLanguage(saved)) {
            return saved
        }
    } catch (error) {
        // private browsing, fall through to the browser's languages
    }

    const preferred = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];

    return matchLanguage(preferred)
}

// A translation's strings laid over the English ones, so anything it leaves
// out still reads in English. Lists are replaced whole, never merged.
const overlay = (base, over) => {
    if (!over || typeof base !== 'object' || Array.isArray(base)) {
        return over === undefined ? base : over
    }

    const result = { ...base };

    Object.keys(over).forEach(key => {
        result[key] = overlay(base[key], over[key])
    })

    return result
}

// Fills the {placeholders} in a string. Values may be elements, so the result
// is a list of nodes for React to render.
export const fill = (template, values) => (
    template.split(/(\{\w+\})/).map((part, index) => {
        const key = part.match(/^\{(\w+)\}$/);

        return key && key[1] in values
            ? <React.Fragment key={index}>{values[key[1]]}</React.Fragment>
            : part
    })
)

// the same, for attributes that need a plain string
export const fillText = (template, values) => (
    template.replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match))
)

const LanguageContext = createContext(null);

export const CVLanguageProvider = ({ children }) => {
    // the language asked for, and the one on screen. They differ only while a
    // translation is on its way; the page stays in the old language meanwhile.
    const [chosen, setChosen] = useState(readLanguage);
    const [shown, setShown] = useState(() => (loaded[chosen] ? chosen : null));

    useEffect(() => {
        let current = true;

        load(chosen).then(
            () => { if (current) setShown(chosen) },
            // offline, say: stay in the language on screen, or English on a first visit
            () => { if (current) setShown(language => language || DEFAULT_LANGUAGE) }
        )

        return () => { current = false }
    }, [chosen])

    const code = shown || DEFAULT_LANGUAGE;
    const language = findLanguage(code);
    const dir = language.dir || 'ltr';

    const t = useMemo(() => (code === DEFAULT_LANGUAGE ? en : overlay(en, loaded[code])), [code]);

    useEffect(() => {
        document.documentElement.lang = code;
        document.documentElement.dir = dir;
    }, [code, dir])

    const choose = useCallback(next => {
        if (!findLanguage(next)) {
            return
        }

        setChosen(next);

        try {
            localStorage.setItem(LANGUAGE_KEY, next);
        } catch (error) {
            // nothing to remember it with, the switch still works for this visit
        }
    }, [])

    const value = useMemo(() => ({ code, dir, name: language.name, t, choose }), [code, dir, language, t, choose]);

    // A first visit in another language waits the moment it takes to fetch
    // its strings, rather than flashing the page in English.
    if (!shown) {
        return null
    }

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    )
}

export const useCVLanguage = () => useContext(LanguageContext)

// A price from the config, in the reader's language: $9.99, 9,99 $US, US$9.99
export const formatPrice = (code, amount) => (
    new Intl.NumberFormat(code, { style: 'currency', currency: PRICES.currency }).format(amount)
)

// A date written as 2026-09-11, the way the reader's language writes dates
export const formatDate = (code, iso) => (
    new Intl.DateTimeFormat(code, { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`))
)
